// frontend/src/components/arrivals/SlidePanel.jsx
import React, { useRef, useLayoutEffect } from 'react';
import { formatIsoTime } from '@core/domain/time';
import { getArrivalStatus } from '@core/domain/arrivals';
import { arrivalPresentation } from '@core/config/arrivalPresentation';
import { useStopArrival } from '../../hooks/useStopArrival';
import Legend from '../common/Legend';
import BlurOverlay from '../common/BlurOverlay';
import {
  buildPanelData,
  buildScheduleMap,
} from '@core/adapters/LondonArrivalsBoardData';

const NAVBAR_HEIGHT = 'var(--navbar-height)';

export default function SlidePanel({
  open,
  stopData,
  onClose,
  routeId,
  isAuthenticated,
  onLoginClick,
}) {
  const { data, loading, error } = useStopArrival(open ? stopData : null, routeId);
  const scrollRef = useRef(null);

  const safeData = buildPanelData(stopData, data);
  const { arrivals, schedules, stopName, timingPointCode, direction, trackColor } = safeData;

  // Auto-scroll to the bottom when arrivals data populates
  useLayoutEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [arrivals]);

  if (!open) return null;

  if (loading) {
    return (
      <>
        <Backdrop onClick={onClose} />
        <Panel>
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
            Loading arrivals…
          </div>
        </Panel>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Backdrop onClick={onClose} />
        <Panel>
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--live-red)' }}>
            Error: {error}
          </div>
        </Panel>
      </>
    );
  }

  const scheduleMap = buildScheduleMap(schedules);

  return (
    <>
      <Backdrop onClick={onClose} />
      <Panel>
        {/* Header – with extra right padding for close button */}
        <div style={{ padding: '24px 60px 24px 24px', borderBottom: '1px solid var(--border-subtle)', position: 'relative' }}>
          <CloseButton onClick={onClose} />
          <h2 style={{ margin: 0, fontSize: 22, color: 'var(--text-primary)', fontWeight: 700 }}>
            {stopName}
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 15, color: 'var(--text-secondary)' }}>
            {timingPointCode}
          </p>
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'inline-block', background: trackColor, color: 'var(--text-on-accent)', fontSize: 10, textTransform: 'uppercase', fontWeight: 700, padding: '4px 8px', borderRadius: 4, letterSpacing: 0.5 }}>
              {direction}
            </div>
            <Legend rows={[
              { items: arrivalPresentation.legend.arrival },
            ]} />
          </div>
        </div>

        {/* Table body – wrapped in BlurOverlay */}
        <BlurOverlay isAuthenticated={isAuthenticated} onLoginClick={onLoginClick}>
          <div ref={scrollRef} style={{ overflowY: 'auto', flex: 1 }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr>
                  {['Trip', 'Run', 'Bonnet', 'Sche', 'Live'].map(label => (
                    <th key={label} style={{
                      position: 'sticky',
                      top: 0,
                      backgroundColor: 'var(--bg-surface)',
                      zIndex: 1,
                      paddingTop: 16,
                      fontSize: 12,
                      textTransform: 'uppercase',
                      color: 'var(--text-muted)',
                      fontWeight: 700,
                      paddingBottom: 5,
                      borderBottom: '2px solid var(--border-subtle)',
                      textAlign: 'center',
                    }}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {arrivals.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                      No upcoming arrivals tracked.
                    </td>
                  </tr>
                ) : (
                  arrivals.map(row => {
                    const tripKey = row.trip_no_lbsl;
                    const schedTime = scheduleMap[tripKey] || '--:--';
                    const liveTime = formatIsoTime(row.expected_arrival);
                    const status = getArrivalStatus(schedTime, liveTime);
                    return (
                      <tr key={tripKey}>
                        <td style={{ padding: '5px 0', fontSize: 11, fontFamily: 'var(--font-mono)', textAlign: 'center' }}>{tripKey}</td>
                        <td style={{ padding: '5px 0', fontSize: 11, fontFamily: 'var(--font-mono)', textAlign: 'center' }}>{row.running_no || '-'}</td>
                        <td style={{ padding: '5px 0', fontSize: 11, fontFamily: 'var(--font-mono)', textAlign: 'center' }}>{row.bonnet_no}</td>
                        <td style={{ padding: '5px 0', fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', textAlign: 'center' }}>{schedTime}</td>
                        <td style={{
                          padding: '5px 0',
                          fontSize: 11,
                          fontFamily: 'var(--font-mono)',
                          fontWeight: 600,
                          color: status.color,
                          textAlign: 'center',
                        }}>
                          {liveTime}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </BlurOverlay>
      </Panel>
    </>
  );
}

// ---- Helper components ----

function Backdrop({ onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        position: 'fixed',
        top: NAVBAR_HEIGHT,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'var(--overlay)',
        opacity: 1,
        pointerEvents: 'auto',
        zIndex: 999,
      }}
    />
  );
}

function Panel({ children }) {
  return (
    <div
      style={{
        position: 'fixed',
        top: NAVBAR_HEIGHT,
        right: 0,
        height: `calc(100vh - ${NAVBAR_HEIGHT})`,
        width: 360,
        background: 'var(--bg-surface)',
        boxShadow: 'var(--shadow-lg)',
        transform: 'translateX(0)',
        transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {children}
    </div>
  );
}

function CloseButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        position: 'absolute',
        top: 24,
        right: 24,
        background: 'transparent',
        border: 'none',
        fontSize: 24,
        color: 'var(--text-muted)',
        cursor: 'pointer',
        padding: '4px 8px',
        borderRadius: 4,
        transition: 'background 0.2s',
      }}
    >
      &times;
    </button>
  );
}